import {
  packSlotDisplayPrice,
  type PackMinPriceSlotLike
} from './hoteles'

/** Label de precio de un pack de habitación (RoomCard). */
export type RoomPackPriceLike = {
  price?: number
  price_label?: string
  total_price?: number
  total_price_label?: string
  specific_taxes?: number
  specific_taxes_list?: unknown[]
}

export function resolveSpecificTaxes(input: {
  amount?: number | null
  listLength?: number | null
}): { has_specific_taxes: boolean; specific_taxes_amount: number } {
  const raw = Number(input.amount)
  const specific_taxes_amount = Number.isFinite(raw) && raw > 0 ? raw : 0
  return {
    has_specific_taxes: specific_taxes_amount > 0 || (input.listLength ?? 0) > 0,
    specific_taxes_amount
  }
}

/**
 * Tasas del pack de ficha (Api2): el universo del AB real-price-display.
 * Si no viene `specific_taxes`, usa el delta `total_price - price`.
 */
export function specificTaxesFromRoomPack(
  pack: RoomPackPriceLike | undefined
): { has_specific_taxes: boolean; specific_taxes_amount: number } {
  const fromField = Number(pack?.specific_taxes)
  const fieldAmount = Number.isFinite(fromField) && fromField > 0 ? fromField : 0
  const price = Number(pack?.price)
  const total = Number(pack?.total_price)
  const fromDelta =
    Number.isFinite(price) && Number.isFinite(total) && total > price
      ? parseFloat((total - price).toFixed(2))
      : 0

  return resolveSpecificTaxes({
    amount: fieldAmount > 0 ? fieldAmount : fromDelta,
    listLength: pack?.specific_taxes_list?.length
  })
}

/**
 * Normaliza labels de precio para comparar SearchCard / RoomCard / checkout
 * (espacios, NBSP, coma vs punto decimal).
 */
export function normalizePriceLabel(label: string | null | undefined): string {
  if (!label) return ''
  return label
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, '')
    .replace(',', '.')
    .trim()
    .toLowerCase()
}

export function priceLabelsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const na = normalizePriceLabel(a)
  const nb = normalizePriceLabel(b)
  if (!na || !nb) return false
  return na === nb
}

/**
 * Mismo formato que devuelve la API para EUR: coma decimal y sin ceros
 * finales (7,7€ / 58,99€ / 104€).
 */
export function formatPriceLabel(amount: number, symbol = '€'): string {
  return `${String(parseFloat(amount.toFixed(2))).replace('.', ',')}${symbol}`
}

/** Total final que debe mostrarse: precio de habitación + tasas específicas. */
export function expectedFinalTotal(price: number, specificTaxes: number): number {
  return parseFloat((price + specificTaxes).toFixed(2))
}

/**
 * Detecta el fallback silencioso: en variante test se pide el total con tasas
 * pero Api2 no lo envía, así que la UI acaba mostrando el precio sin tasas.
 */
export function usesPriceFallbackInTestVariant(
  pack: RoomPackPriceLike | undefined,
  useTotalPrice: boolean
): boolean {
  if (!useTotalPrice) return false
  return typeof pack?.total_price !== 'number' && !pack?.total_price_label
}

/** Mismo criterio que RoomCard `precio` en variante real-price-display. */
export function roomPackDisplayPriceLabel(
  pack: RoomPackPriceLike | undefined,
  useTotalPrice: boolean
): string {
  if (useTotalPrice && pack?.total_price_label) {
    return String(pack.total_price_label)
  }
  return String(pack?.price_label ?? '')
}

/**
 * Labels visibles en las tres superficies para un mismo pack/habitación.
 * Checkout siempre usa el total final (`totalPriceFormat`); search/room
 * dependen del flag `real-price-display`.
 */
export function resolveVisibleFinalPriceLabels(input: {
  useTotalPrice: boolean
  /** pack_min_price del hotel (SearchCard "desde"). */
  searchPackSlot?: PackMinPriceSlotLike
  /** packs[N] de la habitación (RoomCard). */
  roomPack?: RoomPackPriceLike
  /** totalPriceFormat del calculate-prices (checkout). */
  checkoutTotalPriceFormat: string
}): {
  searchLabel: string
  roomLabel: string
  checkoutLabel: string
} {
  const searchLabel = packSlotDisplayPrice(
    input.searchPackSlot,
    input.useTotalPrice
  ).label
  const roomLabel = roomPackDisplayPriceLabel(
    input.roomPack,
    input.useTotalPrice
  )
  return {
    searchLabel: searchLabel?.trim() ?? '',
    roomLabel: roomLabel?.trim() ?? '',
    checkoutLabel: input.checkoutTotalPriceFormat?.trim() ?? ''
  }
}

/**
 * En variante test, todos los labels visibles deben coincidir con el total
 * del checkout. Si un label no está visible (vacío), no entra en la comparación.
 */
export function visibleFinalPricesAreCoherent(input: {
  useTotalPrice: boolean
  searchPackSlot?: PackMinPriceSlotLike
  roomPack?: RoomPackPriceLike
  checkoutTotalPriceFormat: string
}): { ok: boolean; labels: ReturnType<typeof resolveVisibleFinalPriceLabels>; reason?: string } {
  const labels = resolveVisibleFinalPriceLabels(input)

  if (!input.useTotalPrice) {
    return {
      ok: true,
      labels,
      reason: 'control: no se exige paridad con total del checkout'
    }
  }

  const visible = [
    { surface: 'search', label: labels.searchLabel },
    { surface: 'room', label: labels.roomLabel },
    { surface: 'checkout', label: labels.checkoutLabel }
  ].filter((item) => normalizePriceLabel(item.label))

  if (visible.length < 2) {
    return {
      ok: false,
      labels,
      reason: 'faltan labels visibles para comparar coherencia'
    }
  }

  const reference = visible.find((item) => item.surface === 'checkout')?.label
    ?? visible[0]!.label

  for (const item of visible) {
    if (!priceLabelsMatch(item.label, reference)) {
      return {
        ok: false,
        labels,
        reason: `${item.surface} (${item.label}) != checkout/ref (${reference})`
      }
    }
  }

  return { ok: true, labels }
}
